import { CashierShiftResult, useRepositoryMutation, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { CommanderFormProps, useDialogForm, useHandledDialogFormControls } from '@mews-commander/form';
import { createZodFormResolver, lt, NumberService, showSuccessAlert, useCurrencyCultureInfo } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Banner, Stack, StackItem } from '@optimus-web/core';
import { LocalizedTypography, useEnterpriseNavigate } from '@mews-commander/ui';
import { ShiftOverviewTable } from '../../tables/ShiftOverviewTable';
import { selectCashierData } from '../../../../settings/queries/useCashierData';
import {
    CashierCloseShiftFormValues,
    getCashierCloseShiftFormDefaultValues,
    getCashierCloseShiftFormValidationSchema,
} from './form/formMapping';
import { CashierCloseShiftFormContent } from './form/CashierCloseShiftFormContent';

interface Params {
    cashierId: string;
}

const CashierCloseShiftForm = ({ cashierId, ...props }: Params & CommanderFormProps) => {
    const navigate = useEnterpriseNavigate();
    const { mutateAsync } = useRepositoryMutation('cashiers', 'closeShift', {
        invalidateQueries: {
            cashiers: [
                'getShift',
                'getClosedShifts',
                'getShiftTransactions',
                'getCurrentShiftTransactions',
                'getCurrentShift',
            ],
        },
        onSuccess: async (data: CashierShiftResult) => {
            showSuccessAlert({
                description: lt('CashierShiftCloseSuccessMessage'),
            });
            await navigate({
                to: 'Finance/Cashier/PastShift/$shiftId',
                params: { shiftId: data.id },
                search: { cashierId },
            });
        },
    });
    const { data } = useRepositorySuspenseQuery(
        'cashiers',
        'getAll',
        { cashierIds: [cashierId] },
        { select: selectCashierData }
    );
    const singleCashier = data[0];
    const enabledCurrencies = singleCashier?.enabledCurrencies?.map((it) => it.code || '') ?? [];
    const localizationInfo = useCurrencyCultureInfo();

    const onSubmit = async (values: CashierCloseShiftFormValues) => {
        await mutateAsync({
            cashierId,
            baseValues: Object.entries(values.currencies).map(([key, value]) => ({
                currency: key,
                value: NumberService.unformat(value, localizationInfo),
            })),
            notes: values.notes,
        });
    };

    const { renderFormContent } = useHandledDialogFormControls<CashierCloseShiftFormValues>({
        ...props,
        resolver: createZodFormResolver(getCashierCloseShiftFormValidationSchema(enabledCurrencies, localizationInfo)),
        onSubmit,
        defaultValues: getCashierCloseShiftFormDefaultValues(enabledCurrencies, localizationInfo),
    });

    return renderFormContent(<CashierCloseShiftFormContent enabledCurrencies={enabledCurrencies} />);
};

const CashierCloseShiftDialogContent = ({ cashierId, ...props }: Params & CommanderFormProps) => (
    <Stack vertical spacing="300" distribution="stretch">
        <StackItem>
            <Banner variant="info" secondaryText={lt('CashierCloseShiftHint')} />
        </StackItem>
        <StackItem>
            <LocalizedTypography textKey={TextKey.CashierShiftOverview} textStyle="titleSmallStrong" as="h4" />
        </StackItem>
        <StackItem>
            <ShiftOverviewTable cashierId={cashierId} />
        </StackItem>
        <StackItem>
            <LocalizedTypography textKey={TextKey.BaseValueCorrection} textStyle="titleSmallStrong" as="h4" />
        </StackItem>
        <StackItem>
            <CashierCloseShiftForm cashierId={cashierId} {...props} />
        </StackItem>
    </Stack>
);

export const useCashierCloseShiftDialog = (cashierId: string) =>
    useDialogForm({
        id: 'cashier-close-shift-dialog',
        title: lt('BaseValueCorrection'),
        saveLabel: lt('CashierShiftClose'),
        renderForm: (props) => <CashierCloseShiftDialogContent cashierId={cashierId} {...props} />,
    });
